import { useState } from "react"
import { Link } from "react-router-dom"

import Layout from "../../components/Layout"
import { TalentFilterSheet } from "@/components/filters/TalentFilterSheet"
import { emptyBrowseFilter } from "@/utils/browseFilter"
import type { BrowseFilter } from "@/shared/types"
import { FormRichText } from "@/components/forms/FormRichText"
import { Heading } from "@/components/typography/heading"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Separator } from "@/components/ui/separator"
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar"
import { toast } from "@/components/ui/toast"
import { Toggle } from "@/components/ui/toggle"
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group"
import {
  ContentTagsCell,
  CreatorCell,
  NotesPreviewCell,
  PitchStatusBadge,
  PlatformCell,
  PriceOrStatusCell,
  RowActions,
  TableShell,
  type TableColumn,
} from "@/components/tableKit"
import type { PitchStatus, Platform, Talent } from "@/shared/types"

const SHORT_NOTES = {
  type: "doc",
  content: [
    {
      type: "paragraph",
      content: [{ type: "text", text: "UK dance audience." }],
    },
  ],
}

const SAMPLE_NOTES = {
  type: "doc",
  content: [
    {
      type: "paragraph",
      content: [
        { type: "text", text: "Strong dance audience in the UK. " },
        { type: "text", marks: [{ type: "bold" }], text: "Easy to brief" },
        { type: "text", text: " and " },
        { type: "text", marks: [{ type: "italic" }], text: "quick to respond" },
        { type: "text", text: ". See " },
        {
          type: "text",
          marks: [{ type: "link", attrs: { href: "https://example.com/brief" } }],
          text: "the brief",
        },
        { type: "text", text: "." },
      ],
    },
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          text: "Has posted similar song promos and can turn a brief around this week without extra usage.",
        },
      ],
    },
    {
      type: "bulletList",
      content: [
        {
          type: "listItem",
          content: [
            {
              type: "paragraph",
              content: [{ type: "text", text: "Prefers usage under 6 months" }],
            },
          ],
        },
        {
          type: "listItem",
          content: [
            {
              type: "paragraph",
              content: [{ type: "text", text: "Can post this week" }],
            },
          ],
        },
      ],
    },
    {
      type: "orderedList",
      content: [
        {
          type: "listItem",
          content: [
            {
              type: "paragraph",
              content: [{ type: "text", text: "First cut by Friday" }],
            },
          ],
        },
        {
          type: "listItem",
          content: [
            {
              type: "paragraph",
              content: [{ type: "text", text: "Live post Monday" }],
            },
          ],
        },
      ],
    },
  ],
}

const SCRIPT_NOTES = {
  type: "doc",
  content: [
    {
      type: "paragraph",
      content: [
        {
          type: "text",
          marks: [{ type: "link", attrs: { href: "javascript:alert(1)" } }],
          text: "click me",
        },
      ],
    },
  ],
}

type PreviewPlatform = {
  username: string
  followers: number
}

type PreviewRow = {
  id: string
  handle: string
  country: string
  managerEmail: string
  imgUrl: string
  status: PitchStatus | "ineligible" | "available"
  price: number | null
  guidePrice?: number
  pitcherName?: string
  tiktok?: PreviewPlatform
  instagram?: PreviewPlatform
  youtube?: PreviewPlatform
  tags: string[]
  notes?: Record<string, unknown> | string
}

const INITIAL_ROWS: PreviewRow[] = [
  {
    id: "available",
    handle: "alexrivera",
    country: "United States",
    managerEmail: "sam@grail-talent.com",
    imgUrl: "https://i.pravatar.cc/80?u=grail-shared-ui-1",
    status: "available",
    price: 850,
    guidePrice: 900,
    tiktok: { username: "alexrivera", followers: 240_000 },
    instagram: { username: "alex.rivera", followers: 118_000 },
    tags: ["Dance", "Comedy", "Lifestyle"],
    notes: SHORT_NOTES,
  },
  {
    id: "pitched",
    handle: "jordanlee",
    country: "United Kingdom",
    managerEmail: "pat@grail-talent.com",
    imgUrl: "https://i.pravatar.cc/80?u=grail-shared-ui-2",
    status: "pitched",
    price: 1200,
    pitcherName: "Pat Manager",
    tiktok: { username: "jordanlee", followers: 510_000 },
    tags: ["Music", "Lipsync"],
    notes: SAMPLE_NOTES,
  },
  {
    id: "needs-info",
    handle: "mikachen",
    country: "Canada",
    managerEmail: "lee@grail-talent.com",
    imgUrl: "https://i.pravatar.cc/80?u=grail-shared-ui-3",
    status: "needs-info",
    price: 600,
    youtube: { username: "mikachen", followers: 88_000 },
    tags: ["Makeup & Beauty"],
    notes: "Legacy plain note from before rich text.",
  },
  {
    id: "approved",
    handle: "devonok",
    country: "Australia",
    managerEmail: "ada@grail-talent.com",
    imgUrl: "https://i.pravatar.cc/80?u=grail-shared-ui-4",
    status: "approved",
    price: 1500,
    tiktok: { username: "devonok", followers: 1_200_000 },
    instagram: { username: "devon.ok", followers: 640_000 },
    tags: ["Fashion", "Travel", "Lifestyle", "Reviews"],
    notes: SCRIPT_NOTES,
  },
  {
    id: "rejected",
    handle: "rileywest",
    country: "Germany",
    managerEmail: "kim@grail-talent.com",
    imgUrl: "https://i.pravatar.cc/80?u=grail-shared-ui-5",
    status: "rejected",
    price: 400,
    instagram: { username: "rileywest", followers: 42_000 },
    tags: ["Fitness"],
  },
  {
    id: "ineligible",
    handle: "caseypark",
    country: "Spain",
    managerEmail: "jo@grail-talent.com",
    imgUrl: "https://i.pravatar.cc/80?u=grail-shared-ui-6",
    status: "ineligible",
    price: null,
    tiktok: { username: "caseypark", followers: 9_000 },
    tags: ["Gaming/Tech"],
  },
]

const SIDEBAR_ITEMS = [
  "Campaign Basics",
  "Brief",
  "Creator Eligibility",
  "Extras",
] as const

export default function SharedUi() {
  const [notes, setNotes] = useState<Record<string, unknown>>(SAMPLE_NOTES)
  const [rows, setRows] = useState(INITIAL_ROWS)
  const [filtersOpen, setFiltersOpen] = useState(false)
  const [filters, setFilters] = useState<BrowseFilter>({
    ...emptyBrowseFilter,
    countries: ["United Kingdom"],
    gender: ["Female"],
    TikTok: { followersRange: { lower: 50_000, upper: 500_000 } },
  })
  const [sidebarSection, setSidebarSection] =
    useState<(typeof SIDEBAR_ITEMS)[number]>("Campaign Basics")
  const [emphasis, setEmphasis] = useState(false)

  const columns: TableColumn<PreviewRow>[] = [
    {
      id: "creator",
      header: "Creator",
      cell: (row) => (
        <CreatorCell talent={previewTalent(row)} />
      ),
    },
    {
      id: "price",
      header: "Price / status",
      cell: (row) => (
        <PriceOrStatusCell
          status={row.status}
          price={row.price}
          guidePrice={row.guidePrice}
          pitcherName={row.pitcherName}
          prices={{
            brandPriceTikTok: row.guidePrice,
            brandPriceInstagramReel: row.guidePrice,
          }}
          platforms={["TikTok", "Instagram"]}
          campaignType="Brand"
          creatorLabel={row.handle}
          onPriceChange={(price) =>
            setRows((current) =>
              current.map((item) =>
                item.id === row.id ? { ...item, price } : item
              )
            )
          }
        />
      ),
    },
    platformColumn("TikTok"),
    platformColumn("Instagram"),
    platformColumn("YouTube"),
    {
      id: "content",
      header: "Content",
      cell: (row) => <ContentTagsCell tags={row.tags} />,
    },
    {
      id: "notes",
      header: "Notes",
      cell: (row) => <NotesPreviewCell notes={row.notes} />,
    },
    {
      id: "status",
      header: "Status",
      cell: (row) =>
        row.status === "available" ? null : (
          <PitchStatusBadge status={row.status} />
        ),
    },
    {
      id: "actions",
      header: "Actions",
      sticky: true,
      cell: (row) => (
        <RowActions
          actions={[
            {
              label: "Edit pitch",
              onSelect: () =>
                toast.add({ type: "success", title: `Edit ${row.handle}` }),
            },
            {
              label: "Needs info",
              onSelect: () =>
                toast.add({ title: `Needs info: ${row.handle}` }),
            },
            {
              label: "Reject",
              destructive: true,
              onSelect: () =>
                toast.add({ type: "error", title: `Reject ${row.handle}` }),
            },
          ]}
        />
      ),
    },
  ]

  return (
    <Layout>
      {/* relative + height bound so stock offcanvas (fixed/h-svh) can be
          pinned to Layout main instead of the viewport. */}
      <SidebarProvider className="relative min-h-[calc(100vh-10rem)] min-w-0 overflow-x-hidden">
        <SharedUiShell
          section={sidebarSection}
          onSectionChange={setSidebarSection}
        >
            <div>
              <Heading type="h1" className="mb-2">
                Shared UI
              </Heading>
              <p className="mb-4 max-w-2xl text-muted-foreground">
                Commit 8 primitives — rich text, compound table kit, filter
                sheet, and sidebar chrome nested in Layout. Admin-only;
                nothing writes to Firestore.
              </p>
            </div>

            <section>
              <Heading type="h2" className="mb-4">
                FormRichText
              </Heading>
              <FormRichText
                label="Notes for Brand"
                description="Bold, italic, lists, and link only. Value is TipTap JSON."
                value={notes}
                onChange={setNotes}
              />
            </section>

            <section>
              <Heading type="h2" className="mb-4">
                TableShell + cells
              </Heading>
              <TableShell
                columns={columns}
                rows={rows}
                getRowId={(row) => row.id}
                empty="No rows"
              />
            </section>

            <section>
              <Heading type="h2" className="mb-4">
                TalentFilterSheet
              </Heading>
              <p className="mb-3 text-sm text-muted-foreground">
                Price chips on. Audience chips start with UK + Female; TikTok
                followers 50K–500K.
              </p>
              <Button type="button" onClick={() => setFiltersOpen(true)}>
                Open filters
              </Button>
              <TalentFilterSheet
                open={filtersOpen}
                onOpenChange={setFiltersOpen}
                value={filters}
                onChange={setFilters}
                platforms={["TikTok", "Instagram"]}
                showPriceChips
                title="Filters"
                description="AND + sectioned chips. Done closes; Clear all resets."
                countryList={[
                  "United Kingdom",
                  "United States",
                  "Canada",
                  "Germany",
                ]}
                tagList={["Dance", "Comedy", "Lifestyle", "Music"]}
                cityList={["London", "Los Angeles", "Toronto"]}
              />
            </section>

            <section>
              <Heading type="h2" className="mb-4">
                Toggle
              </Heading>
              {/* Row of toolbar-style toggles. */}
              <div className="mb-3 flex flex-wrap">
                <Toggle
                  size="sm"
                  pressed={emphasis}
                  onPressedChange={setEmphasis}
                  aria-label="Emphasis"
                >
                  Emphasis
                </Toggle>
              </div>
              <ToggleGroup defaultValue={["list"]}>
                <ToggleGroupItem value="list" aria-label="List">
                  List
                </ToggleGroupItem>
                <ToggleGroupItem value="grid" aria-label="Grid">
                  Grid
                </ToggleGroupItem>
              </ToggleGroup>
            </section>
        </SharedUiShell>
      </SidebarProvider>
    </Layout>
  )
}

function previewTalent(row: PreviewRow): Talent {
  return {
    id: row.id,
    name: row.handle,
    country: row.country,
    imgUrl: row.imgUrl,
    managers: [{ email: row.managerEmail }],
    contentTags: row.tags,
    platformData: {
      ...(row.tiktok
        ? { TikTok: { username: row.tiktok.username, followers: row.tiktok.followers } }
        : {}),
      ...(row.instagram
        ? {
            Instagram: {
              username: row.instagram.username,
              followers: row.instagram.followers,
            },
          }
        : {}),
      ...(row.youtube
        ? { YouTube: { username: row.youtube.username, followers: row.youtube.followers } }
        : {}),
    },
  }
}

function platformColumn(platform: Platform): TableColumn<PreviewRow> {
  return {
    id: platform,
    header: platform,
    cell: (row) => <PlatformCell platform={platform} talent={previewTalent(row)} />,
  }
}

function SidebarSections({
  active,
  onSelect,
}: {
  active: (typeof SIDEBAR_ITEMS)[number]
  onSelect: (item: (typeof SIDEBAR_ITEMS)[number]) => void
}) {
  return (
    <SidebarContent>
      <SidebarGroup>
        <SidebarGroupLabel>Sections</SidebarGroupLabel>
        <SidebarMenu>
          {SIDEBAR_ITEMS.map((item) => (
            <SidebarMenuItem key={item}>
              <SidebarMenuButton
                isActive={active === item}
                onClick={() => onSelect(item)}
              >
                {item}
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    </SidebarContent>
  )
}

function SharedUiShell({
  section,
  onSectionChange,
  children,
}: {
  section: (typeof SIDEBAR_ITEMS)[number]
  onSectionChange: (item: (typeof SIDEBAR_ITEMS)[number]) => void
  children: React.ReactNode
}) {
  return (
    <>
      <Sidebar className="absolute inset-y-0 h-full">
        <SidebarSections active={section} onSelect={onSectionChange} />
      </Sidebar>
      <SidebarInset className="min-w-0">
        {/* Figma create header: 64px bar, 16px pad, 28px trigger, 8px then
            a 16px rule, 16px to crumbs. Same chrome on every breakpoint. */}
        <header className="flex h-16 shrink-0 items-center border-b px-4">
          <div className="flex items-center gap-2">
            <SidebarTrigger className="size-7" />
            <Separator
              orientation="vertical"
              className="mr-2 data-vertical:h-4 data-vertical:self-center"
            />
            <Breadcrumb>
              <BreadcrumbList className="gap-1.5">
                <BreadcrumbItem>
                  <BreadcrumbLink render={<Link to="/dev/preview" />}>
                    New Campaign
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbPage>{section}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </div>
        </header>
        <div className="min-w-0 space-y-10 p-4">{children}</div>
      </SidebarInset>
    </>
  )
}
